import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { FaultSchema } from './payments';

/* ------------------------------------------------------------------ */
/* Support + refunds (W16)                                             */
/*                                                                     */
/* Customers chat in-app (order › Help, Account › Help); partners and   */
/* riders chat + "request a call". A rules-first bot answers first and  */
/* refunds clear cases instantly (Capsico Money via W11); everything    */
/* else goes to an agent in Capsico OS /support (support realm). Agents */
/* refund within their limit, pick who pays (restaurant payout line,    */
/* rider earnings penalty, Capsico), leads approve bigger refunds, QA   */
/* resolved tickets and read the reports.                               */
/* ------------------------------------------------------------------ */

const Id = z.uuid();

/** Polling fallback when the live socket is down (ticket view, inbox). */
export const SUPPORT_POLL_SECONDS = 15;
/** Sweep cadence (job:support-sweep, cron every minute). */
export const SUPPORT_SWEEP_SECONDS = 60;
/** Longest chat message. */
export const SUPPORT_MESSAGE_MAX = 1000;
/** Most photos on one ticket message. */
export const SUPPORT_MAX_ATTACHMENTS = 4;
/** Largest photo (bytes). */
export const SUPPORT_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
export const SUPPORT_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/* ---------------- languages ---------------- */

export const SUPPORT_LANGUAGES = ['en', 'hi', 'ne'] as const;
export const SupportLanguageSchema = z.enum(SUPPORT_LANGUAGES);
export type SupportLanguage = z.infer<typeof SupportLanguageSchema>;

export const SUPPORT_LANGUAGE_LABELS: Record<SupportLanguage, string> = {
  en: 'English',
  hi: 'हिन्दी',
  ne: 'नेपाली',
};

/** +91 → Hindi / English agents; +977 → Nepali / English agents. The first is the default. */
export const SUPPORT_LANGUAGE_POOLS: Record<'IN' | 'NP', readonly SupportLanguage[]> = {
  IN: ['hi', 'en'],
  NP: ['ne', 'en'],
};

/* ---------------- who asks ---------------- */

export const SUPPORT_REQUESTER_REALMS = ['customer', 'partner', 'rider'] as const;
export const SupportRequesterRealmSchema = z.enum(SUPPORT_REQUESTER_REALMS);
export type SupportRequesterRealm = z.infer<typeof SupportRequesterRealmSchema>;

export const SUPPORT_CHANNELS = ['app', 'whatsapp'] as const;
export const SupportChannelSchema = z.enum(SUPPORT_CHANNELS);
export type SupportChannel = z.infer<typeof SupportChannelSchema>;

/* ---------------- reason codes ---------------- */

export const SUPPORT_REASON_GROUPS = [
  'order',
  'delivery',
  'payment',
  'conduct',
  'partner',
  'rider',
  'other',
] as const;
export const SupportReasonGroupSchema = z.enum(SUPPORT_REASON_GROUPS);
export type SupportReasonGroup = z.infer<typeof SupportReasonGroupSchema>;

export const SUPPORT_REASON_GROUP_LABELS: Record<SupportReasonGroup, string> = {
  order: 'Food & items',
  delivery: 'Delivery',
  payment: 'Payments & refunds',
  conduct: 'Behaviour',
  partner: 'Restaurant partners',
  rider: 'Riders',
  other: 'Other',
};

/**
 * What the bot may settle on its own:
 *  - missing_items: refund the picked items when a photo is attached and the delivery proof shows
 *    the code / door was verified (within the auto-refund cap, claim ratio and risk checks);
 *  - refund_status: the order was cancelled and already refunded → explain;
 *  - charged_twice: the gateway shows a duplicate and it is already going back → explain;
 *  - late: the late credit was already given (W14) → explain;
 *  - lead_queue: straight to the lead queue (disputes by restaurants / riders);
 *  - none: an agent answers.
 */
export const SUPPORT_AUTO_RULES = [
  'none',
  'missing_items',
  'refund_status',
  'charged_twice',
  'late',
  'lead_queue',
] as const;
export const SupportAutoRuleSchema = z.enum(SUPPORT_AUTO_RULES);
export type SupportAutoRule = z.infer<typeof SupportAutoRuleSchema>;

export const LIABILITIES = ['restaurant', 'rider', 'capsico'] as const;
export const LiabilitySchema = FaultSchema;
export type Liability = z.infer<typeof LiabilitySchema>;

export const LIABILITY_LABELS: Record<Liability, string> = {
  restaurant: 'Restaurant',
  rider: 'Rider',
  capsico: 'Capsico',
};

/** How the default liability may move with the evidence. */
export const SUPPORT_LIABILITY_MODES = ['fixed', 'seal', 'detour'] as const;
export const SupportLiabilityModeSchema = z.enum(SUPPORT_LIABILITY_MODES);
export type SupportLiabilityMode = z.infer<typeof SupportLiabilityModeSchema>;

export const SUPPORT_SEVERITIES = ['low', 'normal', 'high', 'critical'] as const;
export const SupportSeveritySchema = z.enum(SUPPORT_SEVERITIES);
export type SupportSeverity = z.infer<typeof SupportSeveritySchema>;

export const ReasonCodeKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{2,39}$/, 'Use lower-case letters, digits and _ (3–40)');

export const SupportReasonCodeSchema = z.object({
  code: ReasonCodeKeySchema,
  label: z.string(),
  /** Short help shown under the reason in the apps. */
  hint: z.string().nullable(),
  group: SupportReasonGroupSchema,
  realms: z.array(SupportRequesterRealmSchema),
  needsPhoto: z.boolean(),
  needsItems: z.boolean(),
  needsOrder: z.boolean(),
  autoRule: SupportAutoRuleSchema,
  liabilityDefault: LiabilitySchema,
  liabilityMode: SupportLiabilityModeSchema,
  slaMinutes: z.number().int(),
  severity: SupportSeveritySchema,
  active: z.boolean(),
  sortOrder: z.number().int(),
  version: z.number().int(),
});
export type SupportReasonCode = z.infer<typeof SupportReasonCodeSchema>;

export const SupportReasonCodeListSchema = z.object({ rows: z.array(SupportReasonCodeSchema) });
export type SupportReasonCodeList = z.infer<typeof SupportReasonCodeListSchema>;

export const ReasonCodeCreateInputSchema = z.object({
  code: ReasonCodeKeySchema,
  label: z.string().trim().min(3).max(80),
  hint: z.string().trim().max(160).nullable().optional(),
  group: SupportReasonGroupSchema,
  realms: z.array(SupportRequesterRealmSchema).min(1).max(3),
  needsPhoto: z.boolean().default(false),
  needsItems: z.boolean().default(false),
  needsOrder: z.boolean().default(true),
  autoRule: SupportAutoRuleSchema.default('none'),
  liabilityDefault: LiabilitySchema.default('capsico'),
  liabilityMode: SupportLiabilityModeSchema.default('fixed'),
  slaMinutes: z.number().int().min(5).max(10_080).default(60),
  severity: SupportSeveritySchema.default('normal'),
  sortOrder: z.number().int().min(0).max(1000).default(100),
});
export type ReasonCodeCreateInput = z.infer<typeof ReasonCodeCreateInputSchema>;

export const ReasonCodeUpdateInputSchema = ReasonCodeCreateInputSchema.omit({ code: true })
  .partial()
  .extend({ expectedVersion: z.number().int().min(1), active: z.boolean().optional() });
export type ReasonCodeUpdateInput = z.infer<typeof ReasonCodeUpdateInputSchema>;

/* ---------------- canned replies (templates) ---------------- */

export const TemplateKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{2,39}$/, 'Use lower-case letters, digits and _ (3–40)');

export const SupportTemplateSchema = z.object({
  id: Id,
  key: TemplateKeySchema,
  language: SupportLanguageSchema,
  title: z.string(),
  /** Placeholders: {name}, {order}, {amount}, {eta}. */
  body: z.string(),
  reasonCode: z.string().nullable(),
  /** Offered when resolving (ends the chat). */
  resolves: z.boolean(),
  active: z.boolean(),
  version: z.number().int(),
});
export type SupportTemplate = z.infer<typeof SupportTemplateSchema>;

export const SupportTemplateListSchema = z.object({ rows: z.array(SupportTemplateSchema) });
export type SupportTemplateList = z.infer<typeof SupportTemplateListSchema>;

export const SupportTemplateQuerySchema = z.object({
  language: SupportLanguageSchema.optional(),
  reasonCode: z.string().max(40).optional(),
  includeInactive: z.coerce.boolean().default(false),
});
export type SupportTemplateQuery = z.infer<typeof SupportTemplateQuerySchema>;

export const TemplateCreateInputSchema = z.object({
  key: TemplateKeySchema,
  language: SupportLanguageSchema,
  title: z.string().trim().min(3).max(80),
  body: z.string().trim().min(3).max(SUPPORT_MESSAGE_MAX),
  reasonCode: ReasonCodeKeySchema.nullable().optional(),
  resolves: z.boolean().default(false),
});
export type TemplateCreateInput = z.infer<typeof TemplateCreateInputSchema>;

export const TemplateUpdateInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  title: z.string().trim().min(3).max(80).optional(),
  body: z.string().trim().min(3).max(SUPPORT_MESSAGE_MAX).optional(),
  reasonCode: ReasonCodeKeySchema.nullable().optional(),
  resolves: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type TemplateUpdateInput = z.infer<typeof TemplateUpdateInputSchema>;

/* ---------------- tickets ---------------- */

export const TICKET_STATUSES = [
  'bot',
  'queued',
  'assigned',
  'waiting_customer',
  'resolved',
  'closed',
] as const;
export const TicketStatusSchema = z.enum(TICKET_STATUSES);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  bot: 'With the assistant',
  queued: 'Waiting for an agent',
  assigned: 'Agent replying',
  waiting_customer: 'Waiting for you',
  resolved: 'Resolved',
  closed: 'Closed',
};

/** Open = not resolved and not closed. */
export const OPEN_TICKET_STATUSES: readonly TicketStatus[] = [
  'bot',
  'queued',
  'assigned',
  'waiting_customer',
];

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const TicketPrioritySchema = z.enum(TICKET_PRIORITIES);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;

export const MESSAGE_SENDERS_SUPPORT = [
  'customer',
  'partner',
  'rider',
  'agent',
  'bot',
  'system',
] as const;
export const SupportSenderSchema = z.enum(MESSAGE_SENDERS_SUPPORT);
export type SupportSender = z.infer<typeof SupportSenderSchema>;

export const SupportAttachmentViewSchema = z.object({
  id: Id,
  contentType: z.string(),
  /** Short-lived link (null until uploaded). */
  url: z.string().nullable(),
});
export type SupportAttachmentView = z.infer<typeof SupportAttachmentViewSchema>;

export const SupportMessageSchema = z.object({
  id: Id,
  ticketId: Id,
  sender: SupportSenderSchema,
  /** Agent first name for agent messages; null otherwise. */
  senderName: z.string().nullable(),
  /** Phone numbers are replaced with "[number hidden]" before anyone outside support sees them. */
  body: z.string(),
  attachments: z.array(SupportAttachmentViewSchema),
  /** Agents only: internal notes are never sent to apps. */
  internal: z.boolean(),
  at: z.string(),
});
export type SupportMessage = z.infer<typeof SupportMessageSchema>;

export const SupportRefundSummarySchema = z.object({
  refundId: Id,
  amountMinor: z.number().int(),
  status: z.string(),
  destination: z.string().nullable(),
  at: z.string(),
});
export type SupportRefundSummary = z.infer<typeof SupportRefundSummarySchema>;

/** What the apps show (customer / partner / rider). */
export const TicketViewSchema = z.object({
  id: Id,
  number: z.string(),
  status: TicketStatusSchema,
  statusLabel: z.string(),
  reasonCode: z.string(),
  reasonLabel: z.string(),
  orderId: Id.nullable(),
  orderNumber: z.string().nullable(),
  language: SupportLanguageSchema,
  channel: SupportChannelSchema,
  /** "Typically replies in 5 min", or the morning ETA when the desk is closed. */
  etaText: z.string().nullable(),
  deskOpen: z.boolean(),
  /** Queue position while waiting for an agent (1 = next). */
  queuePosition: z.number().int().nullable(),
  agentName: z.string().nullable(),
  refunds: z.array(SupportRefundSummarySchema),
  canReply: z.boolean(),
  canRate: z.boolean(),
  csatScore: z.number().int().nullable(),
  callRequestedAt: z.string().nullable(),
  messages: z.array(SupportMessageSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int(),
});
export type TicketView = z.infer<typeof TicketViewSchema>;

export const TicketSummarySchema = z.object({
  id: Id,
  number: z.string(),
  status: TicketStatusSchema,
  statusLabel: z.string(),
  reasonLabel: z.string(),
  orderNumber: z.string().nullable(),
  lastMessage: z.string().nullable(),
  unread: z.boolean(),
  updatedAt: z.string(),
});
export type TicketSummary = z.infer<typeof TicketSummarySchema>;

export const TicketListSchema = z.object({ rows: z.array(TicketSummarySchema) });
export type TicketList = z.infer<typeof TicketListSchema>;

export const ReasonPickerQuerySchema = z.object({ orderId: Id.optional() });
export type ReasonPickerQuery = z.infer<typeof ReasonPickerQuerySchema>;

export const SupportOrderItemSchema = z.object({
  orderItemId: Id,
  name: z.string(),
  quantity: z.number().int(),
  lineTotalMinor: z.number().int(),
});
export type SupportOrderItem = z.infer<typeof SupportOrderItemSchema>;

/** The Help hub: reasons for this person (and order), the order's items for the picker. */
export const ReasonPickerSchema = z.object({
  reasons: z.array(
    SupportReasonCodeSchema.pick({
      code: true,
      label: true,
      hint: true,
      group: true,
      needsPhoto: true,
      needsItems: true,
      needsOrder: true,
    }),
  ),
  items: z.array(SupportOrderItemSchema),
  orderNumber: z.string().nullable(),
  deskOpen: z.boolean(),
  deskHoursText: z.string(),
  /** Open ticket on this order (go to it instead of opening another). */
  openTicketId: Id.nullable(),
});
export type ReasonPicker = z.infer<typeof ReasonPickerSchema>;

export const PickedItemSchema = z.object({
  orderItemId: Id,
  quantity: z.number().int().min(1).max(50),
});
export type PickedItem = z.infer<typeof PickedItemSchema>;

export const TicketCreateInputSchema = z.object({
  orderId: Id.optional(),
  reasonCode: ReasonCodeKeySchema,
  items: z.array(PickedItemSchema).max(30).default([]),
  attachmentIds: z.array(Id).max(SUPPORT_MAX_ATTACHMENTS).default([]),
  message: z.string().trim().max(SUPPORT_MESSAGE_MAX).optional(),
  language: SupportLanguageSchema.optional(),
});
export type TicketCreateInput = z.infer<typeof TicketCreateInputSchema>;

export const TicketMessageInputSchema = z
  .object({
    body: z.string().trim().max(SUPPORT_MESSAGE_MAX).default(''),
    attachmentIds: z.array(Id).max(SUPPORT_MAX_ATTACHMENTS).default([]),
  })
  .refine((value) => value.body.length > 0 || value.attachmentIds.length > 0, {
    message: 'Type a message or add a photo',
    path: ['body'],
  });
export type TicketMessageInput = z.infer<typeof TicketMessageInputSchema>;

export const TicketLanguageInputSchema = z.object({ language: SupportLanguageSchema });
export type TicketLanguageInput = z.infer<typeof TicketLanguageInputSchema>;

export const CsatInputSchema = z.object({
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().max(300).optional(),
});
export type CsatInput = z.infer<typeof CsatInputSchema>;

export const CallRequestInputSchema = z.object({
  note: z.string().trim().max(200).optional(),
});
export type CallRequestInput = z.infer<typeof CallRequestInputSchema>;

export const AttachmentStartInputSchema = z.object({
  contentType: z.enum(SUPPORT_ATTACHMENT_TYPES),
  sizeBytes: z.number().int().min(1).max(SUPPORT_ATTACHMENT_MAX_BYTES),
});
export type AttachmentStartInput = z.infer<typeof AttachmentStartInputSchema>;

export const AttachmentTicketSchema = z.object({
  attachmentId: Id,
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type AttachmentTicket = z.infer<typeof AttachmentTicketSchema>;

export const AttachmentDoneSchema = z.object({ attachmentId: Id, uploaded: z.boolean() });
export type AttachmentDone = z.infer<typeof AttachmentDoneSchema>;

/* ---------------- agents ---------------- */

export const AGENT_STATUSES = ['online', 'break', 'offline'] as const;
export const AgentStatusSchema = z.enum(AGENT_STATUSES);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

export const AGENT_ROLES = ['support_agent', 'support_lead'] as const;
export const AgentRoleSchema = z.enum(AGENT_ROLES);
export type AgentRole = z.infer<typeof AgentRoleSchema>;

const ShiftTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM')
  .nullable();

export const SupportAgentSchema = z.object({
  identityId: Id,
  name: z.string().nullable(),
  phoneMasked: z.string(),
  role: AgentRoleSchema,
  country: CountryCodeSchema,
  languages: z.array(SupportLanguageSchema),
  /** Reason groups the agent handles (empty = all). */
  skills: z.array(SupportReasonGroupSchema),
  /** Per-action refund limit (minor units); null = the country setting. */
  refundLimitMinor: z.number().int().nullable(),
  effectiveRefundLimitMinor: z.number().int(),
  maxChats: z.number().int().nullable(),
  effectiveMaxChats: z.number().int(),
  status: AgentStatusSchema,
  statusAt: z.string(),
  shiftStart: ShiftTime,
  shiftEnd: ShiftTime,
  openTickets: z.number().int(),
  identityStatus: z.enum(['invited', 'active', 'suspended']),
  version: z.number().int(),
});
export type SupportAgent = z.infer<typeof SupportAgentSchema>;

export const SupportAgentListSchema = z.object({ rows: z.array(SupportAgentSchema) });
export type SupportAgentList = z.infer<typeof SupportAgentListSchema>;

export const AgentInviteInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: z
    .string()
    .trim()
    .regex(/^\+(91[6-9]\d{9}|9779[678]\d{8})$/, 'Enter a valid Indian or Nepali mobile number'),
  role: AgentRoleSchema.default('support_agent'),
  languages: z.array(SupportLanguageSchema).min(1).max(3),
  skills: z.array(SupportReasonGroupSchema).max(7).default([]),
  refundLimitMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
  maxChats: z.number().int().min(1).max(20).nullable().optional(),
  shiftStart: ShiftTime.optional(),
  shiftEnd: ShiftTime.optional(),
});
export type AgentInviteInput = z.infer<typeof AgentInviteInputSchema>;

export const AgentUpdateInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  role: AgentRoleSchema.optional(),
  languages: z.array(SupportLanguageSchema).min(1).max(3).optional(),
  skills: z.array(SupportReasonGroupSchema).max(7).optional(),
  refundLimitMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
  maxChats: z.number().int().min(1).max(20).nullable().optional(),
  shiftStart: ShiftTime.optional(),
  shiftEnd: ShiftTime.optional(),
  suspended: z.boolean().optional(),
});
export type AgentUpdateInput = z.infer<typeof AgentUpdateInputSchema>;

export const AgentStatusInputSchema = z.object({ status: AgentStatusSchema });
export type AgentStatusInput = z.infer<typeof AgentStatusInputSchema>;

/** GET /v1/support/me — the signed-in agent. */
export const AgentMeSchema = SupportAgentSchema.extend({
  isLead: z.boolean(),
  deskOpen: z.boolean(),
  deskHoursText: z.string(),
  currency: z.enum(['INR', 'NPR']),
});
export type AgentMe = z.infer<typeof AgentMeSchema>;

/* ---------------- inbox ---------------- */

export const INBOX_VIEWS = ['mine', 'queue', 'waiting', 'lead', 'all', 'closed'] as const;
export const InboxViewSchema = z.enum(INBOX_VIEWS);
export type InboxView = z.infer<typeof InboxViewSchema>;

export const InboxQuerySchema = z.object({
  view: InboxViewSchema.default('mine'),
  language: SupportLanguageSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  reasonCode: z.string().max(40).optional(),
  search: z.string().trim().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type InboxQuery = z.infer<typeof InboxQuerySchema>;

export const InboxRowSchema = z.object({
  id: Id,
  number: z.string(),
  status: TicketStatusSchema,
  priority: TicketPrioritySchema,
  requesterRealm: SupportRequesterRealmSchema,
  requesterName: z.string().nullable(),
  reasonCode: z.string(),
  reasonLabel: z.string(),
  language: SupportLanguageSchema,
  country: CountryCodeSchema,
  orderNumber: z.string().nullable(),
  outletName: z.string().nullable(),
  assignedAgentId: Id.nullable(),
  assignedAgentName: z.string().nullable(),
  slaDueAt: z.string().nullable(),
  slaBreached: z.boolean(),
  leadQueue: z.boolean(),
  callRequested: z.boolean(),
  lastMessage: z.string().nullable(),
  lastMessageAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int(),
});
export type InboxRow = z.infer<typeof InboxRowSchema>;

export const InboxSchema = z.object({
  rows: z.array(InboxRowSchema),
  counts: z.object({
    mine: z.number().int(),
    queue: z.number().int(),
    waiting: z.number().int(),
    lead: z.number().int(),
    breached: z.number().int(),
  }),
});
export type Inbox = z.infer<typeof InboxSchema>;

/* ---------------- agent ticket detail (order 360) ---------------- */

export const OrderEventLineSchema = z.object({
  action: z.string(),
  toStatus: z.string().nullable(),
  actor: z.string(),
  at: z.string(),
});
export type OrderEventLine = z.infer<typeof OrderEventLineSchema>;

export const Order360Schema = z.object({
  orderId: Id,
  orderNumber: z.string(),
  status: z.string(),
  outletId: Id,
  outletName: z.string(),
  placedAt: z.string(),
  deliveredAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  totalMinor: z.number().int(),
  paymentMethod: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  /** The bill lines the customer saw (W10 bill JSON). */
  bill: z.array(z.object({ label: z.string(), amountMinor: z.number().int() })),
  items: z.array(SupportOrderItemSchema),
  timeline: z.array(OrderEventLineSchema),
  refunds: z.array(
    z.object({
      id: Id,
      amountMinor: z.number().int(),
      reason: z.string(),
      fault: z.string().nullable(),
      status: z.string(),
      destination: z.string().nullable(),
      at: z.string(),
    }),
  ),
  refundRoomMinor: z.number().int(),
  riderId: Id.nullable(),
  riderName: z.string().nullable(),
  proof: z
    .object({
      method: z.string(),
      codeVerified: z.boolean(),
      distanceM: z.number().nullable(),
      sealResult: z.string().nullable(),
      hasPhoto: z.boolean(),
      photoUrl: z.string().nullable(),
      at: z.string(),
    })
    .nullable(),
  /** Rider path from W14 (null when tracking is off or nothing recorded). */
  path: z.array(z.object({ lat: z.number(), lng: z.number(), at: z.string() })).nullable(),
  lateCreditAt: z.string().nullable(),
  promisedToAt: z.string().nullable(),
});
export type Order360 = z.infer<typeof Order360Schema>;

export const RequesterHistorySchema = z.object({
  realm: SupportRequesterRealmSchema,
  name: z.string().nullable(),
  phoneMasked: z.string().nullable(),
  ordersLast30Days: z.number().int(),
  claimsLast30Days: z.number().int(),
  claimRatioPercent: z.number(),
  claimRatioFlag: z.boolean(),
  refundedLast30DaysMinor: z.number().int(),
  recentTickets: z.array(
    z.object({
      id: Id,
      number: z.string(),
      reasonLabel: z.string(),
      status: TicketStatusSchema,
      refundMinor: z.number().int(),
      createdAt: z.string(),
    }),
  ),
});
export type RequesterHistory = z.infer<typeof RequesterHistorySchema>;

export const TICKET_ACTION_KINDS = [
  'created',
  'bot_refund',
  'bot_explained',
  'bot_handoff',
  'assigned',
  'claimed',
  'reassigned',
  'transferred',
  'escalated',
  'refund',
  'goodwill',
  'approval_requested',
  'approved',
  'rejected',
  'liability',
  'rider_charge',
  'call_requested',
  'call_bridged',
  'waiting',
  'resolved',
  'reopened',
  'closed',
  'csat',
  'sla_breach',
  'language',
  'suspend_proposal',
] as const;
export const TicketActionKindSchema = z.enum(TICKET_ACTION_KINDS);
export type TicketActionKind = z.infer<typeof TicketActionKindSchema>;

export const TicketActionSchema = z.object({
  id: Id,
  kind: TicketActionKindSchema,
  actor: z.string(),
  actorName: z.string().nullable(),
  amountMinor: z.number().int().nullable(),
  note: z.string().nullable(),
  at: z.string(),
});
export type TicketAction = z.infer<typeof TicketActionSchema>;

export const AgentTicketSchema = z.object({
  ticket: InboxRowSchema.extend({
    requesterIdentityId: Id,
    liability: LiabilitySchema.nullable(),
    suggestedLiability: LiabilitySchema.nullable(),
    refundTotalMinor: z.number().int(),
    resolution: z.string().nullable(),
    csatScore: z.number().int().nullable(),
    csatComment: z.string().nullable(),
    botOutcome: z.string().nullable(),
    pickedItems: z.array(PickedItemSchema),
    deskOpen: z.boolean(),
  }),
  messages: z.array(SupportMessageSchema),
  order: Order360Schema.nullable(),
  requester: RequesterHistorySchema,
  actions: z.array(TicketActionSchema),
  pendingApproval: z
    .object({ id: Id, amountMinor: z.number().int(), requestedAt: z.string() })
    .nullable(),
  /** Agent's own limit for one refund (minor units). */
  refundLimitMinor: z.number().int(),
  riderChargeCapMinor: z.number().int(),
});
export type AgentTicket = z.infer<typeof AgentTicketSchema>;

/* ---------------- agent actions ---------------- */

export const AgentReplyInputSchema = z.object({
  body: z.string().trim().min(1).max(SUPPORT_MESSAGE_MAX),
  internal: z.boolean().default(false),
  templateId: Id.optional(),
});
export type AgentReplyInput = z.infer<typeof AgentReplyInputSchema>;

export const REFUND_ACTION_KINDS = ['items', 'full', 'goodwill'] as const;
export const RefundActionKindSchema = z.enum(REFUND_ACTION_KINDS);
export type RefundActionKind = z.infer<typeof RefundActionKindSchema>;

export const AgentRefundInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    kind: RefundActionKindSchema,
    items: z.array(PickedItemSchema).max(30).optional(),
    /** Goodwill amount (minor units); items / full are computed by the server. */
    amountMinor: z.number().int().min(100).max(10_000_000).optional(),
    liability: LiabilitySchema,
    note: z.string().trim().min(5, 'Say why (at least 5 letters)').max(300),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'items' && (!value.items || value.items.length === 0)) {
      ctx.addIssue({ code: 'custom', message: 'Pick the items to refund', path: ['items'] });
    }
    if (value.kind === 'goodwill' && value.amountMinor === undefined) {
      ctx.addIssue({ code: 'custom', message: 'Enter the amount', path: ['amountMinor'] });
    }
  });
export type AgentRefundInput = z.infer<typeof AgentRefundInputSchema>;

export const AgentRefundResultSchema = z.object({
  outcome: z.enum(['refunded', 'needs_approval']),
  amountMinor: z.number().int(),
  refundId: Id.nullable(),
  approvalId: Id.nullable(),
  riderChargeMinor: z.number().int(),
  ticket: InboxRowSchema,
});
export type AgentRefundResult = z.infer<typeof AgentRefundResultSchema>;

export const LiabilityInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  liability: LiabilitySchema,
  note: z.string().trim().min(3).max(300),
});
export type LiabilityInput = z.infer<typeof LiabilityInputSchema>;

export const TransferInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    toAgentId: Id.optional(),
    language: SupportLanguageSchema.optional(),
    note: z.string().trim().min(3).max(300),
  })
  .refine((value) => value.toAgentId !== undefined || value.language !== undefined, {
    message: 'Pick an agent or a language queue',
    path: ['toAgentId'],
  });
export type TransferInput = z.infer<typeof TransferInputSchema>;

export const EscalateInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  note: z.string().trim().min(3).max(300),
});
export type EscalateInput = z.infer<typeof EscalateInputSchema>;

export const SUPPORT_CALL_TARGETS = ['requester', 'restaurant', 'rider'] as const;
export const SupportCallTargetSchema = z.enum(SUPPORT_CALL_TARGETS);
export type SupportCallTarget = z.infer<typeof SupportCallTargetSchema>;

export const AgentCallInputSchema = z.object({ to: SupportCallTargetSchema });
export type AgentCallInput = z.infer<typeof AgentCallInputSchema>;

export const AgentCallResultSchema = z.object({
  dialNumber: z.string().nullable(),
  expiresAt: z.string().nullable(),
  provider: z.string(),
  message: z.string().nullable(),
});
export type AgentCallResult = z.infer<typeof AgentCallResultSchema>;

export const ResolveInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  templateId: Id.optional(),
  message: z.string().trim().max(SUPPORT_MESSAGE_MAX).optional(),
  resolution: z.string().trim().min(3).max(300),
});
export type ResolveInput = z.infer<typeof ResolveInputSchema>;

export const VersionInputSchema = z.object({ expectedVersion: z.number().int().min(1) });
export type VersionInput = z.infer<typeof VersionInputSchema>;

/* ---------------- lead: approvals, QA, board, suspensions ---------------- */

export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected'] as const;
export const ApprovalStatusSchema = z.enum(APPROVAL_STATUSES);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

export const SupportApprovalSchema = z.object({
  id: Id,
  ticketId: Id,
  ticketNumber: z.string(),
  orderNumber: z.string().nullable(),
  kind: RefundActionKindSchema,
  amountMinor: z.number().int(),
  liability: LiabilitySchema,
  note: z.string(),
  country: CountryCodeSchema,
  requestedBy: z.string(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  status: ApprovalStatusSchema,
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  version: z.number().int(),
});
export type SupportApproval = z.infer<typeof SupportApprovalSchema>;

export const ApprovalListQuerySchema = z.object({
  status: ApprovalStatusSchema.default('pending'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type ApprovalListQuery = z.infer<typeof ApprovalListQuerySchema>;

export const ApprovalListSchema = z.object({ rows: z.array(SupportApprovalSchema) });
export type ApprovalList = z.infer<typeof ApprovalListSchema>;

export const ApprovalDecisionInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  note: z.string().trim().min(3).max(300),
});
export type ApprovalDecisionInput = z.infer<typeof ApprovalDecisionInputSchema>;

export const QaReviewSchema = z.object({
  id: Id,
  ticketId: Id,
  ticketNumber: z.string(),
  agentId: Id.nullable(),
  agentName: z.string().nullable(),
  reviewerName: z.string().nullable(),
  score: z.number().int(),
  note: z.string().nullable(),
  at: z.string(),
});
export type QaReview = z.infer<typeof QaReviewSchema>;

export const QaQueueRowSchema = z.object({
  ticketId: Id,
  ticketNumber: z.string(),
  reasonLabel: z.string(),
  agentId: Id.nullable(),
  agentName: z.string().nullable(),
  resolvedAt: z.string(),
  refundTotalMinor: z.number().int(),
  csatScore: z.number().int().nullable(),
  review: QaReviewSchema.nullable(),
});
export type QaQueueRow = z.infer<typeof QaQueueRowSchema>;

export const QaQuerySchema = z.object({
  reviewed: z.enum(['no', 'yes', 'all']).default('no'),
  agentId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type QaQuery = z.infer<typeof QaQuerySchema>;

export const QaListSchema = z.object({ rows: z.array(QaQueueRowSchema) });
export type QaList = z.infer<typeof QaListSchema>;

export const QaReviewInputSchema = z.object({
  ticketId: Id,
  score: z.number().int().min(1).max(5),
  note: z.string().trim().max(500).optional(),
});
export type QaReviewInput = z.infer<typeof QaReviewInputSchema>;

export const SuspendProposalSchema = z.object({
  id: Id,
  outletId: Id,
  outletName: z.string(),
  confirmedCount: z.number().int(),
  windowDays: z.number().int(),
  status: z.enum(['pending', 'confirmed', 'dismissed']),
  createdAt: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  version: z.number().int(),
});
export type SuspendProposal = z.infer<typeof SuspendProposalSchema>;

export const SuspendProposalListSchema = z.object({ rows: z.array(SuspendProposalSchema) });
export type SuspendProposalList = z.infer<typeof SuspendProposalListSchema>;

export const LeadBoardSchema = z.object({
  queues: z.array(
    z.object({
      language: SupportLanguageSchema,
      queued: z.number().int(),
      assigned: z.number().int(),
      waiting: z.number().int(),
      oldestQueuedAt: z.string().nullable(),
    }),
  ),
  slaBreaches: z.number().int(),
  leadQueue: z.number().int(),
  pendingApprovals: z.number().int(),
  pendingSuspensions: z.number().int(),
  agents: z.array(
    z.object({
      identityId: Id,
      name: z.string().nullable(),
      status: AgentStatusSchema,
      statusAt: z.string(),
      languages: z.array(SupportLanguageSchema),
      openTickets: z.number().int(),
      maxChats: z.number().int(),
      resolvedToday: z.number().int(),
    }),
  ),
  csatTrend: z.array(
    z.object({ day: z.string(), average: z.number().nullable(), count: z.number().int() }),
  ),
  deskOpen: z.object({ IN: z.boolean(), NP: z.boolean() }),
});
export type LeadBoard = z.infer<typeof LeadBoardSchema>;

/* ---------------- reports ---------------- */

export const SupportReportQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type SupportReportQuery = z.infer<typeof SupportReportQuerySchema>;

export const SupportReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  totals: z.object({
    tickets: z.number().int(),
    botResolved: z.number().int(),
    agentResolved: z.number().int(),
    firstResponseMinutes: z.number().nullable(),
    resolutionMinutes: z.number().nullable(),
    csatAverage: z.number().nullable(),
    csatCount: z.number().int(),
    refundsMinor: z.number().int(),
  }),
  daily: z.array(
    z.object({
      day: z.string(),
      reasonCode: z.string(),
      reasonLabel: z.string(),
      tickets: z.number().int(),
    }),
  ),
  byDay: z.array(
    z.object({
      day: z.string(),
      tickets: z.number().int(),
      firstResponseMinutes: z.number().nullable(),
      resolutionMinutes: z.number().nullable(),
      csatAverage: z.number().nullable(),
    }),
  ),
  refundsByLiability: z.array(
    z.object({
      liability: LiabilitySchema,
      amountMinor: z.number().int(),
      count: z.number().int(),
    }),
  ),
});
export type SupportReport = z.infer<typeof SupportReportSchema>;

/* ---------------- desk hours (Settings › Support) ---------------- */

export const DeskHoursSchema = z.object({
  rows: z.array(
    z.object({
      country: CountryCodeSchema,
      openMinute: z.number().int(),
      closeMinute: z.number().int(),
      timezone: z.string(),
      open: z.boolean(),
      text: z.string(),
    }),
  ),
});
export type DeskHours = z.infer<typeof DeskHoursSchema>;

/* ---------------- live ---------------- */

export const SUPPORT_LIVE_EVENTS = {
  /** A ticket changed (status, assignment, SLA). Payload: SupportTicketEvent. */
  ticket: 'support.ticket',
  /** A new message on a ticket (internal notes only to the agent rooms). Payload: SupportMessage. */
  message: 'support.message',
  /** Queue sizes changed. Payload: SupportQueueEvent. */
  queue: 'support.queue',
  /** An approval was asked for or decided. Payload: SupportApproval. */
  approval: 'support.approval',
} as const;

export const supportRooms = {
  ticket: (ticketId: string) => `ticket:${ticketId}`,
  queue: (language: SupportLanguage | 'all') => `support:queue:${language}`,
};

export const SupportTicketEventSchema = z.object({
  ticketId: Id,
  status: TicketStatusSchema,
  assignedAgentId: Id.nullable(),
  language: SupportLanguageSchema,
  version: z.number().int(),
  at: z.string(),
});
export type SupportTicketEvent = z.infer<typeof SupportTicketEventSchema>;

export const SupportQueueEventSchema = z.object({
  language: SupportLanguageSchema,
  queued: z.number().int(),
  at: z.string(),
});
export type SupportQueueEvent = z.infer<typeof SupportQueueEventSchema>;

/* ---------------- WhatsApp seam ---------------- */

export const WHATSAPP_PROVIDERS = ['none', 'mock'] as const;
export type WhatsAppProviderName = (typeof WHATSAPP_PROVIDERS)[number];
